/**
 * Future Closure Assumptions Log Component
 * P1: Displays detailed traceability log of all configurations and assumptions
 */

import React, { useState } from 'react';

// Type definitions for calculation result
interface GrowthMonth {
  month: number;
  month_name: string;
  growth_pct: number;
}

interface QuarterAdjustment {
  original_revenue: number;
  expected_revenue: number;
  scale_factor: number;
  adjustment_pct: number;
  months: number[];
}

interface MissingPeriod {
  period: string;
  fill_method: string;
  naics: string;
  amount: number;
  source_date?: string;
  is_live_data?: boolean;
}

interface CalculationResult {
  parameters?: {
    closure_duration_months?: number;
    aggression?: number;
    calculated_at?: string;
  };
  base_period_summary?: {
    years_analyzed?: number;
    monthly_average?: number;
    annual_average?: number;
    trend?: string;
  };
  continuation_percentages?: {
    fixed_costs?: number;
    payroll?: number;
    other_variable?: number;
  };
  seasonality_indices?: Record<string, number>;
  one_time_costs?: {
    shutdown?: number;
    restart?: number;
  };
  defensibility_notes?: string[];
  analysis_flags?: {
    event_anomaly?: {
      year: number;
      spike_pct: number;
      concentration: string;
      excluded: boolean;
      excluded_quarters?: string[];
      quarter_adjustments?: Record<string, QuarterAdjustment>;
      total_adjustment?: number;
      normalization_method?: string;
      granular_details?: {
        concentrated_quarters: string[];
        quarterly_growth: Record<string, number>;
        top_growth_months: GrowthMonth[];
      };
    };
    accounting_basis?: {
      type: string;
      lag_days: number;
    };
    payroll_policy?: {
      description: string;
      icw_applied: boolean;
    };
    expense_policy?: {
      entity: string;
      variable_pct: number;
    };
    missing_periods?: MissingPeriod[];
    closure_costs?: {
      shutdown_pct: number;
      restart_pct: number;
    };
    multiplier?: {
      mode: string;
      value: number;
    };
  };
  dual_projections?: {
    sustainable_demand?: number;
    spiked_demand?: number;
  };
  monte_carlo?: {
    iterations?: number;
    p10?: number;
    p50?: number;
    p90?: number;
    mean?: number;
    std_dev?: number;
    summary_text?: string;
    perturbation_config?: {
      trend_variation_pct?: number;
      expense_variation_pct?: number;
      bls_proxy_variation_pct?: number;
    };
  };
}

interface AssumptionsLogProps {
  calculationResult: CalculationResult;
}

export default function FutureClosureAssumptionsLog({ calculationResult }: AssumptionsLogProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!calculationResult) return null;

  const { parameters, base_period_summary, continuation_percentages, seasonality_indices, one_time_costs, defensibility_notes } = calculationResult;

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-amber-50 transition-colors bg-amber-50/50"
      >
        <div className="flex items-center space-x-3">
          <span className="text-2xl">📋</span>
          <div className="text-left">
            <h4 className="font-semibold text-gray-900">Assumptions & Traceability Log</h4>
            <p className="text-sm text-gray-600">Full audit trail for litigation support</p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {isExpanded && (
        <div className="px-6 py-4 space-y-6 bg-amber-50/30">
          {/* Calculation Metadata */}
          <div>
            <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
              <span>🕒</span>
              <span>Calculation Metadata</span>
            </h5>
            <div className="bg-white rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Calculation Date:</span>
                <span className="font-medium">{formatDate(calculationResult.calculation_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Calculation Type:</span>
                <span className="font-medium">Future Forced Closure</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Projection Period:</span>
                <span className="font-medium">{parameters?.closure_duration_months || 36} months</span>
              </div>
            </div>
          </div>

          {/* Key Parameters */}
          <div>
            <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
              <span>⚙️</span>
              <span>Key Parameters</span>
            </h5>
            <div className="bg-white rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-600">Parameter</th>
                    <th className="px-4 py-2 text-right text-gray-600">Value</th>
                    <th className="px-4 py-2 text-left text-gray-600">Rationale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-4 py-2 font-medium">Aggression Level</td>
                    <td className="px-4 py-2 text-right">
                      <span className={`font-bold ${
                        parameters?.aggression < 30 ? 'text-blue-600' :
                        parameters?.aggression < 70 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {parameters?.aggression}/100
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">Controls growth & continuation assumptions</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Base Period</td>
                    <td className="px-4 py-2 text-right">{base_period_summary?.period_type} {base_period_summary?.months_analyzed} months</td>
                    <td className="px-4 py-2 text-gray-600">Historical data used for projections</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Growth Rate</td>
                    <td className="px-4 py-2 text-right">{parameters?.growth_rate}% annual</td>
                    <td className="px-4 py-2 text-gray-600">Based on historical CAGR, capped at defensible max</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Inflation Rate</td>
                    <td className="px-4 py-2 text-right">{parameters?.inflation_rate}% annual</td>
                    <td className="px-4 py-2 text-gray-600">Applied to expense projections</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Closure Duration</td>
                    <td className="px-4 py-2 text-right">{parameters?.closure_duration_months} months</td>
                    <td className="px-4 py-2 text-gray-600">Total remediation period</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Expense Continuation Rates */}
          <div>
            <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
              <span>💰</span>
              <span>Expense Continuation Rates</span>
            </h5>
            <div className="bg-white rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-600">Expense Category</th>
                    <th className="px-4 py-2 text-right text-gray-600">Rate</th>
                    <th className="px-4 py-2 text-left text-gray-600">Justification</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-4 py-2 font-medium">Officer Salary</td>
                    <td className="px-4 py-2 text-right font-bold text-green-600">{continuation_percentages?.officer_salary || 100}%</td>
                    <td className="px-4 py-2 text-gray-600">Management compensation during closure</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Employee Wages</td>
                    <td className="px-4 py-2 text-right font-bold text-green-600">{continuation_percentages?.employee_wages || 100}%</td>
                    <td className="px-4 py-2 text-gray-600">Staff retention to prevent claims</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Rent/Lease</td>
                    <td className="px-4 py-2 text-right font-bold text-green-600">{continuation_percentages?.rent || 100}%</td>
                    <td className="px-4 py-2 text-gray-600">Lease obligations continue</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Utilities</td>
                    <td className="px-4 py-2 text-right font-bold text-yellow-600">{continuation_percentages?.utilities || 25}%</td>
                    <td className="px-4 py-2 text-gray-600">Reduced for standby operations</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Insurance</td>
                    <td className="px-4 py-2 text-right font-bold text-green-600">{continuation_percentages?.insurance || 90}%</td>
                    <td className="px-4 py-2 text-gray-600">Coverage maintained during closure</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Other Expenses</td>
                    <td className="px-4 py-2 text-right font-bold text-yellow-600">{continuation_percentages?.other_expenses || 30}%</td>
                    <td className="px-4 py-2 text-gray-600">Miscellaneous ongoing obligations</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* One-Time Costs */}
          <div>
            <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
              <span>🏗️</span>
              <span>One-Time Costs Methodology</span>
            </h5>
            <div className="bg-white rounded-lg p-4 space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <div>
                  <span className="font-medium">Shutdown Costs</span>
                  <p className="text-xs text-gray-600">{one_time_costs?.shutdown_description || '1.5x annual discretionary expenses'}</p>
                </div>
                <span className="font-bold text-orange-600">${one_time_costs?.shutdown?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <div>
                  <span className="font-medium">Restart Costs</span>
                  <p className="text-xs text-gray-600">{one_time_costs?.restart_description || '3x shutdown cost'}</p>
                </div>
                <span className="font-bold text-green-600">${one_time_costs?.restart?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {one_time_costs?.custom && (
                <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  ⚠️ Custom values provided by user - not calculated from historical data
                </div>
              )}
            </div>
          </div>

          {/* Seasonality Indices */}
          <div>
            <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
              <span>📊</span>
              <span>Seasonality Indices</span>
            </h5>
            <div className="bg-white rounded-lg p-4">
              <div className="grid grid-cols-4 md:grid-cols-6 gap-2 text-sm">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((month) => {
                  const index = seasonality_indices?.[`month_${month}`] || 1.0;
                  const monthName = new Date(2000, month - 1, 1).toLocaleString('default', { month: 'short' });
                  return (
                    <div key={month} className="text-center p-2 rounded bg-gray-50">
                      <div className="text-xs text-gray-500">{monthName}</div>
                      <div className={`font-bold ${
                        index > 1.05 ? 'text-green-600' :
                        index < 0.95 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {index.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Values &gt;1.0 indicate above-average months, &lt;1.0 indicate below-average
              </p>
            </div>
          </div>

          {/* Edge Case Overrides Applied */}
          {calculationResult.analysis_flags && Object.keys(calculationResult.analysis_flags).some(k => calculationResult.analysis_flags[k]) && (
            <div>
              <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                <span>⚠️</span>
                <span>Edge Case Overrides Applied</span>
              </h5>
              <div className="bg-white rounded-lg p-4 space-y-3 text-sm">
                {/* Revenue Anomaly */}
                {calculationResult.analysis_flags.event_anomaly && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-amber-900">Revenue Anomaly:</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        calculationResult.analysis_flags.event_anomaly.excluded 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {calculationResult.analysis_flags.event_anomaly.excluded ? 'Excluded' : 'Included'}
                      </span>
                    </div>
                    <p className="text-amber-800 mt-1">
                      {calculationResult.analysis_flags.event_anomaly.year} +{calculationResult.analysis_flags.event_anomaly.spike_pct}% 
                      {calculationResult.analysis_flags.event_anomaly.concentration !== 'full year' && 
                       calculationResult.analysis_flags.event_anomaly.concentration !== 'distributed across year' &&
                        ` — ${calculationResult.analysis_flags.event_anomaly.concentration}`}
                    </p>
                    
                    {/* P2: Granular Quarter Breakdown */}
                    {calculationResult.analysis_flags.event_anomaly.granular_details && (
                      <div className="mt-2 p-2 bg-amber-100/50 rounded">
                        <div className="text-xs font-medium text-amber-900 mb-1">📊 Quarterly Growth Breakdown:</div>
                        <div className="flex gap-3 text-xs">
                          {Object.entries(calculationResult.analysis_flags.event_anomaly.granular_details.quarterly_growth || {}).map(([quarter, growth]: [string, number]) => {
                            const isConcentrated = calculationResult.analysis_flags.event_anomaly.granular_details.concentrated_quarters?.includes(quarter);
                            return (
                              <span 
                                key={quarter}
                                className={`${isConcentrated ? 'text-red-700 font-bold' : 'text-gray-600'}`}
                              >
                                {quarter}: {growth > 0 ? '+' : ''}{growth}%
                              </span>
                            );
                          })}
                        </div>
                        {calculationResult.analysis_flags.event_anomaly.granular_details.top_growth_months?.length > 0 && (
                          <div className="mt-1 text-xs text-amber-800">
                            Top months: {calculationResult.analysis_flags.event_anomaly.granular_details.top_growth_months
                              .map((m: GrowthMonth) => `${m.month_name} (+${m.growth_pct}%)`)
                              .join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {calculationResult.dual_projections?.sustainable_demand && (
                      <div className="mt-2 text-xs text-gray-600">
                        Sustainable: ${calculationResult.dual_projections.sustainable_demand.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | 
                        Event-included: ${calculationResult.dual_projections.spiked_demand?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    )}
                    
                    {/* NEW: Quarter-Specific Exclusion Details */}
                    {calculationResult.analysis_flags.event_anomaly.excluded && 
                     calculationResult.analysis_flags.event_anomaly.excluded_quarters && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                        <p className="text-xs font-semibold text-red-800">
                          🎯 {calculationResult.analysis_flags.event_anomaly.normalization_method === 'quarter_specific'
                            ? `Quarters Normalized: ${calculationResult.analysis_flags.event_anomaly.excluded_quarters.join(', ')}`
                            : 'Full Year Normalized'}
                        </p>
                        {calculationResult.analysis_flags.event_anomaly.quarter_adjustments && 
                         Object.keys(calculationResult.analysis_flags.event_anomaly.quarter_adjustments).length > 0 && (
                          <div className="mt-1 flex gap-2 flex-wrap text-xs">
                            {Object.entries(calculationResult.analysis_flags.event_anomaly.quarter_adjustments).map(([q, adj]: [string, QuarterAdjustment]) => (
                              <span key={q} className="px-2 py-0.5 bg-red-100 text-red-700 rounded">
                                {q}: {adj.adjustment_pct > 0 ? '+' : ''}{adj.adjustment_pct}%
                              </span>
                            ))}
                          </div>
                        )}
                        {calculationResult.analysis_flags.event_anomaly.total_adjustment !== undefined && (
                          <p className="mt-1 text-xs text-red-700">
                            Total adjustment: ${Math.abs(calculationResult.analysis_flags.event_anomaly.total_adjustment).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            {calculationResult.analysis_flags.event_anomaly.total_adjustment < 0 ? ' reduced' : ' increased'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Accounting Basis */}
                {calculationResult.analysis_flags.accounting_basis && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Accounting Basis:</span>
                    <span className="font-medium">
                      {calculationResult.analysis_flags.accounting_basis.type === 'cash' 
                        ? `Cash — ${calculationResult.analysis_flags.accounting_basis.lag_days > 0 
                            ? `normalized (${calculationResult.analysis_flags.accounting_basis.lag_days}-day lag)` 
                            : 'no adjustment'}`
                        : 'Accrual'}
                    </span>
                  </div>
                )}
                
                {/* Payroll Policy */}
                {calculationResult.analysis_flags.payroll_policy && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Payroll Treatment:</span>
                    <span className="font-medium">
                      {calculationResult.analysis_flags.payroll_policy.description}
                      {calculationResult.analysis_flags.payroll_policy.icw_applied && 
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">ICW</span>
                      }
                    </span>
                  </div>
                )}
                
                {/* Entity/Expense Policy */}
                {calculationResult.analysis_flags.expense_policy && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Entity Type:</span>
                    <span className="font-medium">
                      {calculationResult.analysis_flags.expense_policy.entity.charAt(0).toUpperCase() + 
                       calculationResult.analysis_flags.expense_policy.entity.slice(1)} 
                      ({Math.round(calculationResult.analysis_flags.expense_policy.variable_pct * 100)}% variable)
                    </span>
                  </div>
                )}
                
                {/* Missing Periods */}
                {calculationResult.analysis_flags.missing_periods && calculationResult.analysis_flags.missing_periods.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <span className="font-medium text-red-900">Data Gaps Filled:</span>
                    <ul className="mt-1 text-red-800 text-xs space-y-1">
                      {calculationResult.analysis_flags.missing_periods.slice(0, 5).map((gap: MissingPeriod, idx: number) => (
                        <li key={idx}>• {gap.period} — {gap.fill_method} (NAICS {gap.naics})</li>
                      ))}
                      {calculationResult.analysis_flags.missing_periods.length > 5 && (
                        <li>...and {calculationResult.analysis_flags.missing_periods.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
                
                {/* Closure Costs */}
                {calculationResult.analysis_flags.closure_costs && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Closure Costs:</span>
                    <span className="font-medium">
                      Shutdown {Math.round(calculationResult.analysis_flags.closure_costs.shutdown_pct * 100)}%, 
                      Restart {Math.round(calculationResult.analysis_flags.closure_costs.restart_pct * 100)}%
                      <span className="text-xs text-gray-500 ml-1">
                        ({calculationResult.analysis_flags.closure_costs.source})
                      </span>
                    </span>
                  </div>
                )}
                
                {/* Multiplier */}
                {calculationResult.analysis_flags.multiplier && calculationResult.analysis_flags.multiplier.mode !== 'none' && (
                  <div className="flex justify-between items-center p-2 bg-blue-50 border border-blue-200 rounded">
                    <span className="text-blue-800">Multiplier Applied:</span>
                    <span className="font-bold text-blue-900">
                      {calculationResult.analysis_flags.multiplier.value}× on {calculationResult.analysis_flags.multiplier.applies_to}
                    </span>
                  </div>
                )}
                
                {/* Trend Method */}
                {calculationResult.analysis_flags.trend_method && (
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>Trend Method:</span>
                    <span>
                      {calculationResult.analysis_flags.trend_method.method}
                      {calculationResult.analysis_flags.trend_method.spike_excluded && ' (excluding spike year)'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Monte Carlo Summary */}
          {calculationResult.monte_carlo && (
            <div>
              <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                <span>📊</span>
                <span>Monte Carlo Analysis</span>
              </h5>
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-200">
                {/* Summary Text */}
                {calculationResult.monte_carlo.summary_text && (
                  <p className="font-medium text-indigo-900 mb-3">
                    {calculationResult.monte_carlo.summary_text}
                  </p>
                )}
                
                {/* Percentile Bars */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-20">P10 (Floor):</span>
                    <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-red-400 rounded-full" style={{width: '10%'}}></div>
                    </div>
                    <span className="text-sm font-medium text-red-700 w-24 text-right">
                      ${calculationResult.monte_carlo.p10?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-20">P50 (Median):</span>
                    <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{width: '50%'}}></div>
                    </div>
                    <span className="text-sm font-bold text-green-700 w-24 text-right">
                      ${calculationResult.monte_carlo.p50?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-20">P90 (Ceiling):</span>
                    <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full" style={{width: '90%'}}></div>
                    </div>
                    <span className="text-sm font-medium text-indigo-700 w-24 text-right">
                      ${calculationResult.monte_carlo.p90?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                
                {/* Perturbation Details */}
                {calculationResult.monte_carlo.perturbation_config && (
                  <div className="mt-3 pt-3 border-t border-indigo-200">
                    <p className="text-xs text-gray-500">
                      Simulations: {calculationResult.monte_carlo.iterations?.toLocaleString()} | 
                      Perturbations: ±{calculationResult.monte_carlo.perturbation_config.trend_variation_pct}% trend, 
                      ±{calculationResult.monte_carlo.perturbation_config.expense_variation_pct}% expenses, 
                      ±{calculationResult.monte_carlo.perturbation_config.bls_proxy_variation_pct}% BLS proxy
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Defensibility Notes */}
          <div>
            <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
              <span>✅</span>
              <span>Defensibility Notes</span>
            </h5>
            <div className="bg-white rounded-lg p-4">
              <ul className="space-y-2 text-sm">
                {defensibility_notes?.map((note: string, idx: number) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Export Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
            <span className="text-2xl">💡</span>
            <div className="text-sm">
              <p className="font-medium text-blue-900">Included in PDF Export</p>
              <p className="text-blue-700">All assumptions and traceability data shown above are automatically included in the PDF report for attorney review and litigation support.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
